import React, { useState } from 'react';
import { useToasts } from 'Context';
import { ConfirmDialog } from 'v3/Components/Shared/ConfirmDialog';
import RadioGroup from 'v3/Components/Form/RadioGroup';
import TextArea from 'v3/Components/Form/TextArea';
import { ReviewerLifecycleStatus } from 'v3/Models/Enums';
import { ENDPOINTS } from 'Models/Templates/Routes/Routes';
import { buildEndpoint, useDataPoster } from 'Utilities';
import './ReviewerProfile.ArchiveDialog.scss';

export type ArchiveReasonOption = {
  id: string;
  name: string;
};

type ReviewerProfileArchiveDialogProps = {
  reviewerId: string;
  archiveReasons: ArchiveReasonOption[];
  dialogVisible: boolean;
  toggleDialog: () => void;
  onSuccess: () => void;
};

const ReviewerProfileArchiveDialog: React.FC<ReviewerProfileArchiveDialogProps> = ({
  reviewerId,
  archiveReasons,
  dialogVisible,
  toggleDialog,
  onSuccess,
}) => {
  const toast = useToasts();
  const [archiveReasonId, setArchiveReasonId] = useState<string | undefined>(undefined);
  const [note, setNote] = useState('');
  const [noteError, setNoteError] = useState<string | undefined>();
  const [reasonError, setReasonError] = useState<string | undefined>();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [postData] = useDataPoster(
    buildEndpoint(ENDPOINTS.reviewers.base, reviewerId, 'status'),
    undefined,
    true,
    '3.0'
  );

  const handleClose = () => {
    setArchiveReasonId(undefined);
    setNote('');
    setNoteError(undefined);
    setReasonError(undefined);
    toggleDialog();
  };

  const handleConfirm = async () => {
    setIsSubmitting(true);
    setNoteError(undefined);
    setReasonError(undefined);
    try {
      const resp: any = await postData({
        newStatus: { value: ReviewerLifecycleStatus.Archived },
        archiveReasonId,
        note,
      });
      if (resp?.status === 200) {
        toast.success('Reviewer has been archived');
        onSuccess();
        handleClose();
      } else if (resp?.response?.status === 400) {
        const data = resp.response?.data;
        if (data?.errors) {
          const mappedErrors: Record<string, string> = {};
          Object.entries(data.errors).forEach(([key, val]) => {
            const field = key.charAt(0).toLowerCase() + key.slice(1);
            mappedErrors[field] = (val as string[])[0];
          });
          setReasonError(mappedErrors.archiveReasonId);
          setNoteError(mappedErrors.note);
          toast.error('Please validate the highlighted fields');
        } else {
          toast.error(data?.message ?? 'Failed to archive reviewer. Please try again.');
        }
      } else if (resp?.response?.status === 403) {
        toast.error(resp.response?.data?.message ?? 'You do not have permission to perform this action.');
      } else if ((resp?.response?.status ?? 0) >= 500) {
        toast.error('Failed to archive reviewer. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <ConfirmDialog
      dialogVisible={dialogVisible}
      toggleDialog={handleClose}
      className="archive-reviewer-dialog"
      title="Archive Reviewer"
      primaryButtonText={isSubmitting ? 'Archiving...' : 'Archive'}
      primaryButtonEnabled={!isSubmitting && !!archiveReasonId && note.trim().length > 0}
      OnPrimaryButtonClick={handleConfirm}
      secondaryButtonText="Cancel"
      OnSecondaryButtonClick={handleClose}
    >
      <p className="archive-reviewer-dialog__description">
        Please provide more detail about why this reviewer is being archived.
      </p>
      <RadioGroup
        label="Archive Reason"
        name="archiveReason"
        required
        orientation="column"
        options={archiveReasons.map((reason) => ({ label: reason.name, value: reason.id }))}
        value={archiveReasonId}
        onChange={(value) => {
          setArchiveReasonId(value);
          setReasonError(undefined);
        }}
        error={reasonError}
      />
      <TextArea
        label="Note"
        required
        value={note}
        onChange={(e) => {
          setNote(e.target.value);
          setNoteError(undefined);
        }}
        error={noteError}
        rows={5}
      />
    </ConfirmDialog>
  );
};

export default ReviewerProfileArchiveDialog;
